import * as THREE from 'three'
import { Heightmap } from './heightmap'
import { mulberry32 } from '../core/rng'

const IMENA = [
  'Madrid', 'Valensija', 'Kadiz', 'Irun', 'Barselona', 'Sevilja',
  'Zaragoza', 'Burgos', 'Toledo', 'Malaga', 'Kartahena', 'La Korunja',
  'Salamanka', 'Granada', 'Oviedo', 'Badahoz',
]

export interface Grad {
  ime: string
  pozicija: THREE.Vector3
}

export interface CitiesResult {
  group: THREE.Group
  dispose(): void
}

/**
 * Na proceduralnoj mapi gradovi se biraju na blagom terenu iznad mora,
 * međusobno razmaknuti. Evropa donosi svoj spisak sa stvarnim koordinatama.
 */
export function izaberiGradove(hm: Heightmap, broj = 10): Grad[] {
  const rnd = mulberry32(hm.seed ^ 0x1b873593)
  const half = hm.size / 2
  const minRazmak = hm.size / (broj * 0.42)

  const kandidati: { x: number; z: number; y: number; ocena: number }[] = []
  const korak = Math.max(4, Math.floor(hm.res / 96))
  for (let j = korak; j < hm.res - korak; j += korak) {
    for (let i = korak; i < hm.res - korak; i += korak) {
      const y = hm.at(i, j)
      if (y < 4) continue
      const nagib = hm.slopeAt(i, j)
      if (nagib > 0.26) continue
      const visinaT = y / Math.max(1, hm.maxY)
      // Gradovi vole doline i podnožja, ne vrhove.
      const ocena = (1 - nagib * 2.4) * (1 - Math.abs(visinaT - 0.16) * 1.7) + rnd() * 0.35
      kandidati.push({ x: -half + i * hm.cell, z: -half + j * hm.cell, y, ocena })
    }
  }
  kandidati.sort((a, b) => b.ocena - a.ocena)

  const gradovi: Grad[] = []
  for (const k of kandidati) {
    if (gradovi.length >= broj) break
    let preblizu = false
    for (const g of gradovi) {
      const dx = g.pozicija.x - k.x
      const dz = g.pozicija.z - k.z
      if (dx * dx + dz * dz < minRazmak * minRazmak) {
        preblizu = true
        break
      }
    }
    if (preblizu) continue
    gradovi.push({
      ime: IMENA[gradovi.length % IMENA.length],
      pozicija: new THREE.Vector3(k.x, k.y, k.z),
    })
  }

  return gradovi
}

/** Markeri i natpisi. `skala` određuje koliko su kule krupne na mapi. */
export function buildCities(gradovi: Grad[], skala: number, visinaNatpisa = 0.024): CitiesResult {
  const group = new THREE.Group()
  group.name = 'gradovi'

  const postoljeGeo = new THREE.CylinderGeometry(9 * skala, 11 * skala, 2.4 * skala, 12)
  const stubGeo = new THREE.CylinderGeometry(1.7 * skala, 2.6 * skala, 16 * skala, 8)
  const kapaGeo = new THREE.OctahedronGeometry(3.2 * skala, 0)

  const postoljeMat = new THREE.MeshStandardMaterial({ color: '#2b2c30', roughness: 0.8 })
  const stubMat = new THREE.MeshStandardMaterial({ color: '#d8d2c4', roughness: 0.6 })
  const kapaMat = new THREE.MeshStandardMaterial({
    color: '#ffcf70',
    emissive: '#ffb43c',
    emissiveIntensity: 0.9,
    roughness: 0.35,
  })

  const spriteMats: THREE.SpriteMaterial[] = []

  for (const g of gradovi) {
    const postolje = new THREE.Mesh(postoljeGeo, postoljeMat)
    postolje.position.copy(g.pozicija).y += 1.2 * skala
    postolje.receiveShadow = true
    postolje.castShadow = true
    group.add(postolje)

    const stub = new THREE.Mesh(stubGeo, stubMat)
    stub.position.copy(g.pozicija).y += 10 * skala
    stub.castShadow = true
    group.add(stub)

    const kapa = new THREE.Mesh(kapaGeo, kapaMat)
    kapa.position.copy(g.pozicija).y += 20 * skala
    group.add(kapa)

    const oznaka = napraviOznaku(g.ime, visinaNatpisa)
    oznaka.position.copy(g.pozicija).y += 30 * skala
    spriteMats.push(oznaka.material as THREE.SpriteMaterial)
    group.add(oznaka)
  }

  return {
    group,
    dispose() {
      postoljeGeo.dispose()
      stubGeo.dispose()
      kapaGeo.dispose()
      postoljeMat.dispose()
      stubMat.dispose()
      kapaMat.dispose()
      for (const m of spriteMats) {
        m.map?.dispose()
        m.dispose()
      }
    },
  }
}

function napraviOznaku(tekst: string, visina: number): THREE.Sprite {
  const dpr = 2
  const font = 'bold 30px ui-sans-serif, -apple-system, "Segoe UI", Roboto, sans-serif'
  const meraCanvas = document.createElement('canvas')
  const meraCtx = meraCanvas.getContext('2d')!
  meraCtx.font = font
  const sirinaTeksta = meraCtx.measureText(tekst.toUpperCase()).width

  const padX = 18
  const w = Math.ceil(sirinaTeksta + padX * 2)
  const h = 46

  const canvas = document.createElement('canvas')
  canvas.width = w * dpr
  canvas.height = h * dpr
  const ctx = canvas.getContext('2d')!
  ctx.scale(dpr, dpr)

  ctx.fillStyle = 'rgba(10, 16, 22, 0.72)'
  zaobljeniPravougaonik(ctx, 0.5, 0.5, w - 1, h - 1, 8)
  ctx.fill()
  ctx.strokeStyle = 'rgba(255, 207, 112, 0.45)'
  ctx.lineWidth = 1
  ctx.stroke()

  ctx.font = font
  ctx.fillStyle = '#f2f5f8'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.letterSpacing = '2px'
  ctx.fillText(tekst.toUpperCase(), w / 2, h / 2 + 1)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.minFilter = THREE.LinearFilter
  tex.generateMipmaps = false

  const mat = new THREE.SpriteMaterial({
    map: tex,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  })
  const sprite = new THREE.Sprite(mat)
  sprite.renderOrder = 20
  // sizeAttenuation je uključen podrazumevano; skalu postavlja main po udaljenosti.
  sprite.material.sizeAttenuation = false
  sprite.scale.set(visina * (w / h), visina, 1)
  return sprite
}

function zaobljeniPravougaonik(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}
